import { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import { Button, Spinner } from '../../components/design-system';
import { AdminPage } from './AdminPage';

interface PortraitRow {
  id: string;
  traveler_id: string;
  host_summary: string | null;
  portrait_json: Record<string, unknown>;
  created_at: string;
  profiles: { full_name: string | null } | null;
}

interface BackfillResult {
  scanned: number;
  updated: number;
  skipped_no_conversation: number;
  skipped_no_messages: number;
  skipped_nothing_derived: number;
}

export function AdminPortraits() {
  const { user } = useAuth();
  const [portraits, setPortraits] = useState<PortraitRow[]>([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState<BackfillResult | null>(null);
  const [backfillError, setBackfillError] = useState<string | null>(null);

  function loadPortraits() {
    if (!user) return;
    setDataLoading(true);
    void supabase
      .from('taste_portraits')
      .select('id, traveler_id, host_summary, portrait_json, created_at, profiles!taste_portraits_traveler_id_fkey(full_name)')
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        setPortraits((data as unknown as PortraitRow[]) ?? []);
        setDataLoading(false);
      });
  }

  useEffect(loadPortraits, [user]);

  async function handleBackfill() {
    setBackfilling(true);
    setBackfillError(null);
    setBackfillResult(null);
    const { data, error } = await supabase.functions.invoke('backfill-trip-details');
    setBackfilling(false);
    if (error || data?.error) {
      setBackfillError((data?.error as string | undefined) ?? 'Backfill failed. Please try again.');
      return;
    }
    setBackfillResult(data as BackfillResult);
    loadPortraits();
  }

  return (
    <AdminPage
      title="Portraits"
      description="Alma's taste portraits generated for travelers."
      actions={
        <Button size="sm" variant="secondary" onClick={() => void handleBackfill()} disabled={backfilling}>
          {backfilling ? <Spinner size="sm" /> : 'Backfill missing trip details'}
        </Button>
      }
    >
      {backfillError && <p className="text-sm text-crimson mb-4">{backfillError}</p>}
      {backfillResult && (
        <div className="bg-app-card border border-app-border rounded-lg px-4 py-3 mb-4 text-xs text-app-fg-2 leading-relaxed">
          Scanned {backfillResult.scanned} portraits missing trip details — filled in {backfillResult.updated}.
          {(backfillResult.skipped_no_conversation > 0 || backfillResult.skipped_no_messages > 0 || backfillResult.skipped_nothing_derived > 0) && (
            <>
              {' '}({backfillResult.skipped_no_conversation} had no linked conversation,{' '}
              {backfillResult.skipped_no_messages} had no saved transcript,{' '}
              {backfillResult.skipped_nothing_derived} had nothing derivable from their transcript.)
            </>
          )}
        </div>
      )}
      {dataLoading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : (
        <div className="flex flex-col gap-4">
          {portraits.length === 0 && (
            <p className="text-sm text-app-muted text-center py-12">No portraits yet.</p>
          )}
          {portraits.map((p) => (
            <div key={p.id} className="bg-app-card rounded-xl border border-app-border shadow-sm p-4 flex flex-col gap-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-medium text-app-fg">
                  {p.profiles?.full_name ?? <span className="text-app-muted font-mono text-xs">{p.traveler_id.slice(0, 8)}…</span>}
                </p>
                <span className="text-xs text-app-muted">{new Date(p.created_at).toLocaleDateString()}</span>
              </div>
              {p.host_summary && (
                <div className="bg-gold/8 border border-gold/25 rounded-lg px-3 py-2.5">
                  <p className="text-[10px] uppercase tracking-wide text-gold font-medium mb-1">Alma's read</p>
                  <p className="text-xs text-app-fg-2 leading-relaxed italic">{p.host_summary}</p>
                </div>
              )}
              {p.portrait_json && Object.keys(p.portrait_json).length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(p.portrait_json).map(([k, v]) => {
                    if (!v || (Array.isArray(v) && v.length === 0)) return null;
                    const display = Array.isArray(v) ? (v as string[]).join(', ') : String(v);
                    return (
                      <span key={k} className="bg-app-bg text-app-fg-2 border border-app-border text-xs rounded-full px-2 py-0.5">
                        <span className="font-medium">{k}:</span> {display}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </AdminPage>
  );
}

export default AdminPortraits;
